// Import the RTK Query methods from the React-specific entry point
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { DayOfWeek } from 'features/common/datetimeUtils';
import { getClosureTimeslots } from 'features/workflow_detour/utils';

import {
  CreateScenario,
  EditScenario,
  MetricsRouteData,
  ProcessStatus,
  Scenario,
  ScenarioList,
  ScenarioRunDetails,
} from '../appTypes';
import { urlifyRouteIds } from './apiSlice';
import { getProjectInfoForSlug, selectUserState } from './userSlice';
import { baseQuery } from './utils';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

type ScenarioApiResponse = {
  scenario_id: string;
  scenario_name: string;
  view_date?: string;
  created_by?: string;
  current_run: ScenarioRunDetails;
};

function scenarioWithRunToFlat(scenario: ScenarioApiResponse): Scenario {
  return {
    ...scenario,
    ...scenario.current_run,
  };
}

// Define our single API slice object
export const detourApiSlice = createApi({
  reducerPath: 'detour_api',
  baseQuery,
  tagTypes: ['scenarios'],
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    getScenarios: builder.query<{ scenarios: ScenarioList }, string>({
      query: (project_slug) => {
        return `/detours/scenarios/${project_slug}/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log('getpredicts query results')
        return { scenarios: rawResult.map(scenarioWithRunToFlat) };
      },
      providesTags: [{ type: 'scenarios' }],
    }),
    getScenarioStatus: builder.query<
      { status: ProcessStatus },
      { project_slug: string; scenario_id: string }
    >({
      query: ({ project_slug, scenario_id }) => {
        return `/detours/scenarios/${project_slug}/${scenario_id}/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log('getpredicts query results');
        return { status: scenarioWithRunToFlat(rawResult).status };
      },
      providesTags: [],
    }),
    getScenario: builder.query<
      { scenario: Scenario },
      { project_slug: string; scenario_id: string }
    >({
      query: ({ project_slug, scenario_id }) => {
        return `/detours/scenarios/${project_slug}/${scenario_id}/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log('getpredicts query results');
        return { scenario: scenarioWithRunToFlat(rawResult) };
      },
      providesTags: [],
    }),
    updateViewDateScenario: builder.mutation<
      { scenario: Scenario },
      { project_slug: string; scenario_id: string }
    >({
      query: ({ project_slug, scenario_id }) => {
        return {
          url: `/detours/scenarios/${project_slug}/${scenario_id}/view/`,
          method: 'POST',
        };
      },
      invalidatesTags: [],
    }),
    editScenario: builder.mutation<
      { scenario: Scenario },
      { project_slug: string; scenario_settings: EditScenario }
    >({
      query: ({ project_slug, scenario_settings }) => {
        const body = {
          scenario_id: scenario_settings.scenario_id,
          scenario_name: scenario_settings.scenario_name,
          current_run: {
            ...scenario_settings,
          },
        };
        return {
          url: `/detours/scenarios/${project_slug}/${scenario_settings.scenario_id}/`,
          method: 'PUT',
          body,
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { scenario: scenarioWithRunToFlat(rawResult) };
      },
      invalidatesTags: [{ type: 'scenarios' }],
    }),
    createScenario: builder.mutation<
      { scenario: Scenario },
      { project_slug: string; scenario_settings: CreateScenario }
    >({
      query: ({ project_slug, scenario_settings }) => {
        const body = {
          scenario_name: scenario_settings.scenario_name,
          current_run: {
            ...scenario_settings,
          },
        };
        return {
          url: `/detours/scenarios/${project_slug}/`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { scenario: scenarioWithRunToFlat(rawResult) };
      },
      invalidatesTags: [{ type: 'scenarios' }],
    }),
    deleteScenario: builder.mutation<
      {},
      { project_slug: string; scenario_id: string }
    >({
      query: ({ project_slug, scenario_id }) => {
        return {
          url: `/detours/scenarios/${project_slug}/${scenario_id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [{ type: 'scenarios' }],
    }),
    getScenarioTravelTime: builder.query<
      MetricsRouteData,
      { project_slug: string; uuid: string; routeIds: string[] }
    >({
      query: ({ project_slug, uuid, routeIds }) => {
        const queryString = `/detours/scenarios/${project_slug}/${uuid}/traveltime/${urlifyRouteIds(
          routeIds,
        )}/`;
        return queryString;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return {
          routeIds: arg.routeIds,
          times: rawResult.times,
          bin_size: rawResult.bin_size,
        };
      },
    }),
  }),
});

// Export the auto-generated hook for the `getDates` query endpoint
export const {
  useGetScenariosQuery,
  useCreateScenarioMutation,
  useEditScenarioMutation,
  useGetScenarioStatusQuery,
  useGetScenarioTravelTimeQuery,
  useDeleteScenarioMutation,
  useGetScenarioQuery,
  useUpdateViewDateScenarioMutation,
} = detourApiSlice;

const initialState: {
  scenarioStatus?: ProcessStatus;
  scenarioSegments: string[];
  closureStart: string;
  closureEnd: string;
  daysOfWeek: DayOfWeek[];
  closureCapacity: number;
  max_detours_per_hour?: number;
  scenario_name?: string;
  scenario_run_uuid: string;
  length: number;
  visualize: boolean;
  viewAllModal: boolean;
  routeMetrics: boolean;
  scenarioIsSaving: boolean; // transient state between confirming “Run Scenario” and receiving the updated list from the server.
} = {
  scenarioStatus: undefined,
  scenarioSegments: [],
  closureStart: undefined,
  closureEnd: undefined,
  daysOfWeek: undefined,
  closureCapacity: 0,
  max_detours_per_hour: undefined,
  scenario_name: '',
  scenario_run_uuid: undefined,
  length: undefined,
  visualize: false,
  viewAllModal: false,
  routeMetrics: false,
  scenarioIsSaving: false,
};

export const detourSlice = createSlice({
  name: 'detour',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetExceptSegments: (state) => {
      return {
        ...initialState,
        scenarioSegments: state.scenarioSegments, // preserve
      };
    },
    resetAll: () => initialState,
    editScenario: (state, action) => {
      state.visualize = true;
      const scenario = action.payload as Scenario;
      state.scenario_name = scenario.scenario_name;
      state.closureEnd = scenario.closure_end;
      state.scenarioSegments = scenario.segments;
      state.closureCapacity = Number(scenario.new_capacities[0] || 0);
      state.closureStart = scenario.closure_start;
      state.daysOfWeek = scenario.days_of_week;
      state.scenario_run_uuid = undefined;
      state.scenarioStatus = undefined;
      state.length = scenario.length;
    },
    copyScenario: (state, action) => {
      state.visualize = true;
      const scenario = action.payload as Scenario;
      state.scenarioStatus = undefined;
      state.scenario_name = `Copy of ${scenario.scenario_name}`;
      state.closureEnd = scenario.closure_end;
      state.scenarioSegments = scenario.segments;
      state.closureCapacity = Number(scenario.new_capacities[0] || 0);
      state.closureStart = scenario.closure_start;
      state.daysOfWeek = scenario.days_of_week;
      state.scenario_run_uuid = undefined;
      state.scenarioStatus = undefined;
      state.length = scenario.length;
    },
    setScenarioStatus: (state, action) => {
      if (action.payload !== state.scenarioStatus) {
        state.scenarioStatus = action.payload;
      }
    },
    setVisualize: (state, action) => {
      state.visualize = action.payload;
    },
    setScenarioIsSaving: (state, action) => {
      state.scenarioIsSaving = action.payload;
    },
    setViewAllModal: (state, action) => {
      state.viewAllModal = action.payload;
    },
    setRouteMetrics: (state, action) => {
      state.routeMetrics = action.payload;
    },
    setScenarioSegments: (state, action) => {
      state.scenarioSegments = action.payload;
    },
    setScenarioStart: (state, action) => {
      state.closureStart = action.payload;
    },
    setScenarioEnd: (state, action) => {
      state.closureEnd = action.payload;
    },
    setScenarioDaysOfWeek: (state, action) => {
      state.daysOfWeek = action.payload;
    },
    setScenarioCapacity: (state, action) => {
      state.closureCapacity = action.payload;
    },
    setScenarioMaxDetours: (state, action) => {
      state.max_detours_per_hour = action.payload;
    },
    setScenarioName: (state, action) => {
      state.scenario_name = action.payload;
    },
    setScenarioLength: (state, action) => {
      state.length = action.payload;
    },
    setScenarioRunUUID: (state, action) => {
      state.scenario_run_uuid = action.payload;
    },
  },
});

const { actions } = detourSlice;
export const {
  setScenarioStatus,
  setScenarioSegments,
  setScenarioEnd,
  setScenarioStart,
  setScenarioDaysOfWeek,
  setScenarioCapacity,
  setScenarioMaxDetours,
  setScenarioLength,
  setScenarioName,
  resetAll,
  copyScenario,
  setVisualize,
  setViewAllModal,
  editScenario,
  setRouteMetrics,
  setScenarioRunUUID,
  resetExceptSegments,
  setScenarioIsSaving,
} = actions;

export function getDetourState(rootState) {
  return rootState[detourSlice.name];
}

export function selectVisualize(rootState): boolean {
  return getDetourState(rootState).visualize;
}

export function selectScenarioIsSaving(rootState): boolean {
  return getDetourState(rootState).scenarioIsSaving;
}

export function selectViewAllModal(rootState): boolean {
  return getDetourState(rootState).viewAllModal;
}

export function selectRouteMetrics(rootState): boolean {
  return getDetourState(rootState).routeMetrics;
}

export function selectScenarioStatus(rootState) {
  return getDetourState(rootState).scenarioStatus;
}

export function selectScenarioSegments(rootState) {
  return getDetourState(rootState).scenarioSegments;
}

export function selectScenarioStart(rootState) {
  return getDetourState(rootState).closureStart;
}

const selectScenarioStartDay = (rootState) =>
  getDetourState(rootState).daysOfWeek?.[0] ?? undefined;

const selectScenarioEndDay = (rootState) =>
  getDetourState(rootState).daysOfWeek?.at(-1) ?? undefined;

export const selectScenarioDaysOfWeekInfo = createSelector(
  [(rootState) => getDetourState(rootState).daysOfWeek],
  (daysOfWeek) => ({
    daysOfWeek,
    startDay: daysOfWeek?.[0] ?? undefined,
    endDay: daysOfWeek?.at(-1) ?? undefined,
  }),
);

export function selectScenarioEnd(rootState) {
  return getDetourState(rootState).closureEnd;
}

export function selectScenarioCapacity(rootState): number {
  return getDetourState(rootState).closureCapacity;
}
export function selectScenarioMaxDetours(rootState) {
  return getDetourState(rootState).max_detours_per_hour;
}
export function selectScenarioName(rootState) {
  return getDetourState(rootState).scenario_name;
}

export function selectScenarioLength(rootState) {
  return getDetourState(rootState).length;
}

export function selectScenarioRunUUID(rootState) {
  return getDetourState(rootState).scenario_run_uuid;
}

const selectDetourBinSize = (rootState) => {
  const projectInfo = getProjectInfoForSlug(
    selectUserState(rootState),
    rootState.workflow.project_slug,
  );
  if (projectInfo) {
    return Math.min(...projectInfo.available_bins);
  }
};

export const selectClosureTimeslots = createSelector(
  [
    selectScenarioStart,
    selectScenarioEnd,
    selectScenarioStartDay,
    selectScenarioEndDay,
    selectDetourBinSize,
  ],
  (closureStart, closureEnd, startDay, endDay, binSize) =>
    getClosureTimeslots(closureStart, closureEnd, binSize, startDay, endDay),
);
